/**
 * Utility functions for the LoginModal component
 */

import { CHANGE_USER_PROCESS } from '@core/constants/USER';

import { LOGIN_MODAL_COMPONENT } from './CONSTANTS';

/**
 * Validates if a string contains only whitespace
 * @param {string} value - The string to validate
 * @returns {boolean} - True if the string contains only whitespace, false otherwise
 */
export const isOnlyWhitespace = (value) => {
    return typeof value === 'string' && value.trim().length === 0;
};

/**
 * Validates the login form fields
 * @param {string} username - The username value
 * @param {string} password - The password value
 * @returns {Object} - Validation result with isValid boolean and errors array
 */
export const validateLoginForm = (username, password) => {
    const errors = [];

    // Check if username is empty or only whitespace
    if (!username || isOnlyWhitespace(username)) {
        errors.push('username');
    } else if (username !== username.trim()) {
        // Check if trimming whitespace changes the username
        errors.push('usernameWhitespace');
    }

    // Check if password is empty or only whitespace
    if (!password || isOnlyWhitespace(password)) {
        errors.push('password');
    } else if (password !== password.trim()) {
        // Check if trimming whitespace changes the password
        errors.push('passwordWhitespace');
    }

    return {
        isValid: errors.length === 0,
        errors,
    };
};

/**
 * Clears the form fields and removes error states
 * @param {HTMLElement} form - The form element
 * @param {HTMLElement} usernameInput - The username input element
 * @param {HTMLElement} passwordInput - The password input element
 */
export const clearForm = (form, usernameInput, passwordInput) => {
    if (usernameInput) {
        usernameInput.value = '';
        usernameInput.parentElement.classList.remove('error');
    }

    if (passwordInput) {
        passwordInput.value = '';
        passwordInput.parentElement.classList.remove('error');
    }

    if (form) {
        form.reset();
    }
};

/**
 * Shows error state for form fields
 * @param {Array} errors - Array of field names with errors
 * @param {HTMLElement} usernameInput - The username input element
 * @param {HTMLElement} passwordInput - The password input element
 */
export const showFieldErrors = (errors, usernameInput, passwordInput) => {
    // Clear previous error states
    if (usernameInput?.parentElement) {
        usernameInput.parentElement.classList.remove('error');
    }
    if (passwordInput?.parentElement) {
        passwordInput.parentElement.classList.remove('error');
    }

    // Apply error states
    errors.forEach((field) => {
        if (field === 'username' && usernameInput?.parentElement) {
            usernameInput.parentElement.classList.add('error');
        } else if (field === 'password' && passwordInput?.parentElement) {
            passwordInput.parentElement.classList.add('error');
        } else if (field === 'usernameWhitespace' && usernameInput?.parentElement) {
            usernameInput.parentElement.classList.add('error');
        } else if (field === 'passwordWhitespace' && passwordInput?.parentElement) {
            passwordInput.parentElement.classList.add('error');
        }
    });
};

/**
 * Toggles password visibility
 * @param {HTMLElement} passwordInput - The password input element
 * @param {HTMLElement} passwordIcon - The password visibility icon element
 */
export const togglePasswordVisibility = (passwordInput, passwordIcon) => {
    if (!passwordInput || !passwordIcon) return;

    const isVisible = passwordInput.type === 'text';
    passwordInput.type = isVisible ? 'password' : 'text';
    passwordIcon.textContent = isVisible ? 'visibility_off' : 'visibility';
};

/**
 * Authorize the user. Open the new login modal.
 * @returns {void}
 */
export const authorize = () => {
    let loginModal = document.querySelector(LOGIN_MODAL_COMPONENT);

    if (!loginModal) {
        document.body.appendChild(document.createElement(LOGIN_MODAL_COMPONENT));
        loginModal = document.querySelector(LOGIN_MODAL_COMPONENT);
    }

    loginModal.showModal();
};

/**
 * Check if the user is authorized. If not, authorize the user.
 */
export const checkAuthorize = () => {
    const changeUser = localStorage.getItem(CHANGE_USER_PROCESS);

    if (changeUser && changeUser === 'true') {
        localStorage.removeItem(CHANGE_USER_PROCESS);
        authorize();
    }
};
